<template>
    <div class="mt-3" @input="updateValue($event)">
        <h3>{{ trans('locations.structure.title') }}</h3>
        <p style="margin-bottom: 1.125rem">
            {{ trans('locations.structure.description') }}
        </p>
        <p v-if="translationHintLanguage" class="small text-muted" style="margin-bottom: 1.125rem">
            {{ trans('locations.attributes.translation_hint', { language: translationHintLanguage }) }}
        </p>
        <div>
            <card body-classes="py-0" card-classes="mb-3" type="decent">
                <span class="d-inline-flex align-items-center text-primary"
                    >+&nbsp;
                    <jet-button class="btn-text p-0" type="button" variant="tertiary" @click.stop="addLocation(locationId)"
                        >{{ trans('locations.actions.add_specific_location') }}
                    </jet-button></span
                >
            </card>
        </div>
        <jet-form-section v-for="(specificLocation, specificIndex) in locationTree" :key="specificLocation.id" class="loc-tree" type="decent">
            <template #form>
                <div class="d-md-flex" style="gap: 60px">
                    <div class="mb-2 text-nowrap" style="font-weight: 900; line-height: 32px">
                        {{ trans('locations.specific_location.label') }}<em class="text-danger">*</em>
                    </div>
                    <div class="w-100">
                        <div>
                            <div class="d-flex align-items-center" style="gap: 12px">
                                <input
                                    :id="`loc-${specificLocation.id}`"
                                    :class="{
                                        'is-invalid': hasError(specificLocation.id),
                                    }"
                                    :name="`loc-${locationTree.length - 1 - specificIndex}`"
                                    :value="getValue(specificLocation.id, specificLocation.name)"
                                    class="form-control"
                                    required
                                    type="text"
                                    @keyup.enter="blurField"
                                />
                                <jet-button
                                    :destructive="true"
                                    class="d-md-none"
                                    type="button"
                                    variant="tertiary"
                                    @click.stop="deleteLocation(specificLocation, 'specific_location')"
                                >
                                    <icon name="cil-trash" />
                                </jet-button>
                            </div>
                            <div v-if="hasError(specificLocation.id)" class="small text-danger">
                                {{ getErrorMessage(specificLocation.id) }}
                            </div>
                            <div class="d-flex align-items-center mt-2 flex-wrap" style="gap: 10px">
                                <jet-button
                                    class="btn-text p-0"
                                    style="min-width: 0"
                                    type="button"
                                    variant="tertiary"
                                    @click.stop="openAttributes(specificLocation, 1)"
                                >
                                    {{ trans('locations.attributes.manage')
                                    }}<span v-if="managedCount(specificLocation)"> ({{ managedCount(specificLocation) }})</span>
                                </jet-button>
                                <span v-if="needsFrench(specificLocation)" class="small text-warning">
                                    {{ trans('locations.attributes.missing_french') }}
                                </span>
                            </div>
                        </div>
                        <div>
                            <div
                                :class="{
                                    'border-bottom': specificLocation.children.length,
                                }"
                                class="d-flex align-items-center justify-content-between mt-3"
                            >
                                <span class="d-inline-flex align-items-center text-primary"
                                    >+&nbsp;
                                    <jet-button
                                        class="btn-text p-0"
                                        style="min-width: 0"
                                        type="button"
                                        variant="tertiary"
                                        @click.stop="addLocation(specificLocation.id)"
                                        >{{ trans('locations.actions.add_area') }}
                                    </jet-button></span
                                >
                                <button
                                    v-if="specificLocation.children.length"
                                    class="btn btn-text text-small text-uppercase"
                                    type="button"
                                    @click.stop="
                                        toggleAreas(
                                            specificLocation.children,
                                            areAllAreasCollapsed(specificLocation.children) ? 'expand' : 'collapse',
                                        )
                                    "
                                >
                                    <span v-if="areAllAreasCollapsed(specificLocation.children)">{{ trans('locations.actions.expand_area') }}</span>
                                    <span v-else>{{ trans('locations.actions.collapse_area') }}</span>
                                </button>
                            </div>
                            <div
                                v-for="(area, areaIndex) in specificLocation.children"
                                class="loc-area d-md-flex mt-md-3 align-items-start"
                                style="gap: 20px"
                            >
                                <div
                                    class="d-flex align-items-center clickable mb-2"
                                    style="font-weight: 900; line-height: 32px; gap: 6px"
                                    @click.stop="toggleAreas([area])"
                                >
                                    <arrow-down
                                        :class="{
                                            collapsed: isAreaCollapsed(area),
                                        }"
                                        class="loc-collapse-toggle text-primary"
                                    />
                                    <span>{{ trans('locations.area.label') }} {{ areaIndex + 1 }}</span>
                                </div>
                                <div class="grow">
                                    <div class="position-relative">
                                        <input
                                            :id="`loc-${area.id}`"
                                            :class="{
                                                'is-invalid': hasError(area.id),
                                            }"
                                            :name="`loc-${locationTree.length - 1 - specificIndex}-${specificLocation.children.length - 1 - areaIndex}`"
                                            :value="getValue(area.id, area.name)"
                                            class="form-control"
                                            type="text"
                                            @keyup.enter="blurField"
                                        />
                                        <button class="btn-icon loc-delete" type="button" @click.stop="deleteLocation(area, 'area')">
                                            <icon name="cil-x-circle" />
                                        </button>
                                    </div>
                                    <div v-if="hasError(area.id)" class="small text-danger">
                                        {{ getErrorMessage(area.id) }}
                                    </div>
                                    <div class="d-flex align-items-center mt-2 flex-wrap" style="gap: 10px">
                                        <jet-button
                                            class="btn-text p-0"
                                            style="min-width: 0"
                                            type="button"
                                            variant="tertiary"
                                            @click.stop="openAttributes(area, 2)"
                                        >
                                            {{ trans('locations.attributes.manage')
                                            }}<span v-if="managedCount(area)"> ({{ managedCount(area) }})</span>
                                        </jet-button>
                                        <span v-if="needsFrench(area)" class="small text-warning">
                                            {{ trans('locations.attributes.missing_french') }}
                                        </span>
                                    </div>

                                    <div
                                        :class="{
                                            collapsed: isAreaCollapsed(area),
                                        }"
                                        class="subareas-wrapper"
                                    >
                                        <div class="loc-subareas">
                                            <div class="loc-subarea">
                                                <span class="d-inline-flex align-items-center loc-leaf text-primary"
                                                    >+&nbsp;
                                                    <jet-button
                                                        class="btn-text p-0"
                                                        style="min-width: 0"
                                                        type="button"
                                                        variant="tertiary"
                                                        @click.stop="addLocation(area.id)"
                                                        >{{ trans('locations.actions.add_subarea') }}
                                                    </jet-button></span
                                                >
                                            </div>
                                            <div
                                                v-for="(subArea, subAreaIndex) in limitSubAreas(area)"
                                                :key="subArea.id ?? subAreaIndex"
                                                class="loc-subarea"
                                            >
                                                <div class="position-relative loc-leaf">
                                                    <input
                                                        :id="`loc-${subArea.id}`"
                                                        :class="{
                                                            'is-invalid': hasError(subArea.id),
                                                        }"
                                                        :name="`loc-${locationTree.length - 1 - specificIndex}-${specificLocation.children.length - 1 - areaIndex}-${limitSubAreas(area).length - 1 - subAreaIndex}`"
                                                        :value="getValue(subArea.id, subArea.name)"
                                                        class="form-control pe-5"
                                                        type="text"
                                                        @keyup.enter="blurField"
                                                    />
                                                    <button
                                                        class="btn-icon loc-delete"
                                                        type="button"
                                                        @click.stop="deleteLocation(subArea, 'subarea')"
                                                    >
                                                        <icon name="cil-x-circle" />
                                                    </button>
                                                </div>
                                                <div v-if="hasError(subArea.id)" class="small ps-4 text-danger">
                                                    {{ getErrorMessage(subArea.id) }}
                                                </div>
                                                <div class="d-flex align-items-center mt-1 flex-wrap ps-4" style="gap: 10px">
                                                    <jet-button
                                                        class="btn-text p-0"
                                                        style="min-width: 0"
                                                        type="button"
                                                        variant="tertiary"
                                                        @click.stop="openAttributes(subArea, 3)"
                                                    >
                                                        {{ trans('locations.attributes.manage')
                                                        }}<span v-if="managedCount(subArea)"> ({{ managedCount(subArea) }})</span>
                                                    </jet-button>
                                                    <span v-if="needsFrench(subArea)" class="small text-warning">
                                                        {{ trans('locations.attributes.missing_french') }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div v-if="area.children.length > 3" class="ps-4">
                                            <button class="btn btn-text" style="gap: 6px" type="button" @click.stop="toggleSubAreas(area)">
                                                <span v-if="areAllSubAreasVisible(area)">{{ trans('general.action.show_less') }} </span>
                                                <span v-else>{{ trans('general.action.show_all') }} ({{ area.children.length }}) </span>
                                                <arrow-down
                                                    :class="{
                                                        opposite: areAllSubAreasVisible(area),
                                                    }"
                                                    class="loc-collapse-toggle text-primary"
                                                />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <jet-button
                        :destructive="true"
                        class="d-none d-md-flex"
                        style="min-width: 0"
                        type="button"
                        variant="tertiary"
                        @click.stop="deleteLocation(specificLocation, 'specific_location')"
                    >
                        <icon name="cil-trash" />
                        {{ trans('general.action.delete') }}
                    </jet-button>
                </div>
            </template>
        </jet-form-section>
        <location-attribute-modal
            :adjacent-line-ids="modalAdjacentLineIds"
            :adjacent-line-options="modalAdjacentOptions"
            :definitions="definitions"
            :level="modalLevel"
            :model-value="modalValue"
            :show="modalShow"
            @close="modalShow = false"
            @save="onModalSave"
        />
    </div>
</template>
<script setup>
import JetButton from '@/Jetstream/Button.vue';
import Card from '@/Components/Card.vue';
import JetFormSection from '@/Jetstream/FormSection.vue';
import LocationAttributeModal from '@/Components/LocationAttributeModal.vue';
import { getLabelForLocale } from '@/Utils/getLabelForLocale';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { eventBus, events } from '@/eventBus.js';
import ArrowDown from '../../../icons/ArrowDown.vue';
import { $http } from '@/bootstrap.js';

const page = usePage();
const emits = defineEmits(['change']);
const props = defineProps({
    locations: Array,
    locationId: String,
    definitions: { type: Array, default: () => [] },
    adjacentLineOptions: { type: Array, default: () => [] },
});

let changeSet = new Map();

const modalShow = ref(false);
const modalValue = ref({});
const modalLevel = ref(null);
const editingId = ref(null);
const modalAdjacentLineIds = ref([]);
const modalAdjacentOptions = ref([]);

const translationHintLanguage = computed(() => {
    const locale = page.props.locale;
    const fallback = page.props.fallback_locale;
    if (!locale || locale === fallback) {
        return null;
    }
    // Only hint when the active locale actually has a translatable name slot.
    if (!props.definitions.some((definition) => definition.key === `name_${locale}`)) {
        return null;
    }
    return getLabelForLocale(locale);
});

const findOriginal = (id) => props.locations.find((location) => location.id === id);

// Build (or copy) a changeSet entry, seeding name/parentId/managedAttributes from
// the original location so a tree rebuild never drops a node's data.
const ensureChangeSetEntry = (id) => {
    if (changeSet.has(id)) {
        return { ...changeSet.get(id) };
    }

    const original = findOriginal(id);

    return {
        id,
        name: original?.name ?? '',
        parentId: original?.parentId ?? null,
        managedAttributes: { ...(original?.managedAttributes || {}) },
        adjacentLineIds: [...(original?.adjacentLineIds || [])],
        children: [],
        exists: true,
    };
};

const sameManaged = (a, b) => {
    const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
    for (const key of keys) {
        if ((a?.[key] ?? '') !== (b?.[key] ?? '')) {
            return false;
        }
    }
    return true;
};

const sameIds = (a, b) => {
    const setA = new Set(a || []);
    const setB = new Set(b || []);
    if (setA.size !== setB.size) {
        return false;
    }
    for (const value of setA) {
        if (!setB.has(value)) {
            return false;
        }
    }
    return true;
};

const isUnchanged = (entry) => {
    if (!entry || entry.exists === false) {
        return false;
    }

    const original = findOriginal(entry.id);
    if (!original) {
        return false;
    }

    return (
        (original.name ?? '') === (entry.name ?? '') &&
        sameManaged(original.managedAttributes, entry.managedAttributes) &&
        sameIds(original.adjacentLineIds, entry.adjacentLineIds)
    );
};

const getManaged = (node) => {
    const entry = changeSet.get(node.id);
    if (entry && entry.managedAttributes) {
        return entry.managedAttributes;
    }
    return node.managedAttributes || {};
};

const managedCount = (node) => Object.values(getManaged(node)).filter((value) => value !== '' && value != null).length;

const needsFrench = (node) => props.definitions.some((definition) => definition.key === 'name_fr') && !getManaged(node).name_fr;

const getAdjacentLineIds = (node) => {
    const entry = changeSet.get(node.id);
    if (entry && entry.adjacentLineIds) {
        return entry.adjacentLineIds;
    }
    return node.adjacentLineIds || [];
};

const getAdjacentLineIdsById = (id) => {
    const entry = changeSet.get(id);
    if (entry && entry.adjacentLineIds) {
        return entry.adjacentLineIds;
    }
    return findOriginal(id)?.adjacentLineIds || [];
};

const setAdjacentLineIds = (id, ids) => {
    const entry = ensureChangeSetEntry(id);
    entry.adjacentLineIds = [...ids];
    changeSet.set(id, entry);

    if (isUnchanged(changeSet.get(id))) {
        changeSet.delete(id);
    }
};

// Adjacency is symmetric (3↔4), so mirror every change onto the other lane(s)
// — the backend then plain-syncs each lane's own list.
const updateAdjacentLines = (nodeId, ids) => {
    const previous = getAdjacentLineIdsById(nodeId);
    const added = ids.filter((id) => !previous.includes(id));
    const removed = previous.filter((id) => !ids.includes(id));

    setAdjacentLineIds(nodeId, ids);

    added.forEach((otherId) => {
        const current = getAdjacentLineIdsById(otherId);
        if (!current.includes(nodeId)) {
            setAdjacentLineIds(otherId, [...current, nodeId]);
        }
    });
    removed.forEach((otherId) => {
        const current = getAdjacentLineIdsById(otherId);
        setAdjacentLineIds(
            otherId,
            current.filter((value) => value !== nodeId),
        );
    });
};

const adjacentOptionsFor = (node) =>
    props.adjacentLineOptions.filter((option) => option.id !== node.id).map((option) => ({ value: option.id, label: option.name }));

const openAttributes = (node, level) => {
    editingId.value = node.id;
    modalValue.value = { ...getManaged(node) };
    modalLevel.value = level;
    modalAdjacentLineIds.value = getAdjacentLineIds(node);
    modalAdjacentOptions.value = adjacentOptionsFor(node);
    modalShow.value = true;
};

const onModalSave = ({ managedAttributes, adjacentLineIds }) => {
    const id = editingId.value;
    const entry = ensureChangeSetEntry(id);
    entry.managedAttributes = { ...managedAttributes };
    changeSet.set(id, entry);

    // Adjacency (mirrored onto the paired lanes); the outer save action syncs the pivot.
    updateAdjacentLines(id, adjacentLineIds || []);

    if (isUnchanged(changeSet.get(id))) {
        changeSet.delete(id);
    }

    emitUpdate();
    modalShow.value = false;
};

const addLocation = (parentId) => {
    let itemId = crypto.randomUUID();

    changeSet.set(itemId, {
        id: itemId,
        name: '',
        parentId: parentId,
        children: [],
        exists: false,
    });

    // Update parent location children
    let parentLocation = changeSet.get(parentId);
    if (parentLocation) {
        changeSet.set(parentId, {
            ...parentLocation,
            children: [...parentLocation.children, itemId],
        });
    }

    updateLocationTree();
    emitUpdate();

    nextTick(() => {
        document.getElementById(`loc-${itemId}`)?.focus();
    });
};

const removeLocationRecursive = (id) => {
    let location = changeSet.get(id);
    if (location) {
        location.children.forEach((childId) => {
            removeLocationRecursive(childId);
        });

        changeSet.delete(id);
    }
};

const removeLocationFromParent = (parentId, id) => {
    let parentLocation = changeSet.get(parentId);
    if (parentLocation) {
        changeSet.set(parentId, {
            ...parentLocation,
            children: parentLocation.children.filter((childId) => childId !== id),
        });
    }
};

const deleteLocation = (loc, type) => {
    // Exit early if location has assigned assets
    if (loc.totalAssetsCount > 0) {
        $http.delete(route('locations.destroy', loc.id)).catch((error) => {
            eventBus.$emit(events.presentFlashMessage, {
                payload: error.response.data,
            });
        });
        return;
    }

    if (changeSet.has(loc.id) && changeSet.get(loc.id).exists === false) {
        removeLocationRecursive(loc.id);
        // Remove location from parent
        removeLocationFromParent(loc.parentId, loc.id);
        updateLocationTree();

        emitUpdate();
        return;
    }

    // Immediately delete w/o confirmation dialog if no assets are assigned
    if (type === 'subarea') {
        router.delete(route('locations.destroy', loc.id), {
            preserveScroll: true,
            only: ['location', 'locationWithChildren', 'flash'],
        });

        return;
    }

    // Show confirmation dialog
    eventBus.$emit(events.openDeleteLocationModal, {
        locationId: loc.id,
        name: loc.name,
        type: type,
    });
};

const updateValue = (event) => {
    const rawId = event.target.id || '';
    if (!rawId.startsWith('loc-')) {
        return;
    }

    const id = rawId.replace('loc-', '');
    const entry = ensureChangeSetEntry(id);
    entry.name = event.target.value;
    changeSet.set(id, entry);

    // Remove changeset entry if it matches the stored location again
    if (isUnchanged(changeSet.get(id))) {
        changeSet.delete(id);
    }

    emitUpdate();
};

const emitUpdate = () => {
    let changes = Array.from(changeSet.values() || []).map((change) => ({
        id: change.id,
        name: change.name,
        parentId: change.parentId,
        managedAttributes: change.managedAttributes || {},
        adjacentLineIds: change.adjacentLineIds || [],
    }));

    emits('change', changes);
};

const hasError = (locationId) => {
    return !!page.props.errors[locationId];
};

const getErrorMessage = (locationId) => {
    return page.props.errors[locationId]?.[0];
};

const locationTree = ref([]);
const originalTree = computed(() => {
    let obj = {};
    props.locations.forEach((el) => (obj[el.id] = el.name));
    return obj;
});

const updateLocationTree = () => {
    const map = {}; // Create a mapping of each item by its id
    const roots = []; // Array to store root nodes

    const allLocations = [
        ...Array.from(changeSet.values() || []).reverse(), // Reverse map to have new items always on top
        ...props.locations,
    ];

    // Initialize each item with an empty children array
    allLocations.forEach((location) => {
        if (!map[location.id]) {
            map[location.id] = { ...location, children: [] };
        }
    });

    // Build the tree structure
    allLocations.forEach((location) => {
        if (location.parentId === props.locationId) {
            // If location has no parentId, it's a root location
            roots.push(map[location.id]);
        } else {
            // If the location has a parent, add it to the parent's children array
            if (map[location.parentId]) {
                map[location.parentId].children.push(map[location.id]);
            }
        }
    });

    nextTick(() => {
        locationTree.value = roots;
    });
};

const getValue = (id, fallback) => {
    return changeSet.has(id) ? changeSet.get(id).name : fallback;
};

const collapsedAreas = ref(new Set());
const areAllAreasCollapsed = (areas) => {
    let resp = true;
    for (const area of areas) {
        if (!collapsedAreas.value.has(area.id)) {
            resp = false;
            break;
        }
    }
    return resp;
};
const isAreaCollapsed = (area) => {
    return collapsedAreas.value.has(area.id);
};
const toggleAreas = (areas, operation) => {
    for (const area of areas) {
        if (operation === 'expand') {
            collapsedAreas.value.delete(area.id);
        } else if (operation === 'collapse') {
            collapsedAreas.value.add(area.id);
        } else if (collapsedAreas.value.has(area.id)) {
            collapsedAreas.value.delete(area.id);
        } else {
            collapsedAreas.value.add(area.id);
        }
    }
};
const expandedSubAreas = ref(new Set());
const areAllSubAreasVisible = (area) => {
    return expandedSubAreas.value.has(area.id);
};
const toggleSubAreas = (area) => {
    if (expandedSubAreas.value.has(area.id)) {
        expandedSubAreas.value.delete(area.id);
    } else {
        expandedSubAreas.value.add(area.id);
    }
};
const limitSubAreas = (area) => {
    if (expandedSubAreas.value.has(area.id)) {
        return area.children;
    }
    return area.children.slice(0, 3);
};

const blurField = (event) => {
    document.activeElement?.blur();
};

onMounted(() => {
    changeSet.clear();
    updateLocationTree();
});

// Rebuild tree whenever locations change
watch(
    originalTree,
    (newValue) => {
        updateLocationTree();
    },
    { deep: true },
);
</script>
